import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X } from 'lucide-react';
import { TagCategoryWithTags } from '../../types';

interface MobileFilterSheetProps {
  open: boolean;
  onClose: () => void;
  categories: TagCategoryWithTags[];
  selectedTagIds: Set<string>;
  onToggle: (tagId: string) => void;
  onClearAll: () => void;
  tagCounts?: Record<string, number>;
}

/**
 * Bottom sheet for filtering on mobile.
 * Shows all categories with their tags vertically; multi-select; selections
 * commit immediately (URL state) — "Done" just dismisses the sheet.
 */
export const MobileFilterSheet: React.FC<MobileFilterSheetProps> = ({
  open,
  onClose,
  categories,
  selectedTagIds,
  onToggle,
  onClearAll,
  tagCounts,
}) => {
  // Esc to close + lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-black/40"
            onClick={onClose}
            aria-hidden
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Filter courses by tag"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-2xl shadow-2xl max-h-[85vh] flex flex-col"
          >
            <header className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h2 className="font-serif text-lg font-bold text-gray-900">Filters</h2>
              <button
                type="button"
                onClick={onClose}
                className="text-sm font-semibold text-gray-700 hover:text-gray-900 px-2 py-1"
              >
                Done
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
              {categories.map((category) => (
                <section key={category.id}>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">
                    {category.name}
                  </h3>
                  <ul className="space-y-1">
                    {category.tags.map((tag) => {
                      const isSelected = selectedTagIds.has(tag.id);
                      const count = tagCounts?.[tag.id] ?? 0;
                      const isEmpty = count === 0 && !isSelected;
                      return (
                        <li key={tag.id}>
                          <button
                            type="button"
                            onClick={() => onToggle(tag.id)}
                            aria-pressed={isSelected}
                            disabled={isEmpty}
                            className={`w-full flex items-center gap-3 py-2.5 text-left ${
                              isEmpty ? 'opacity-40 cursor-not-allowed' : ''
                            }`}
                          >
                            <span
                              className={`h-5 w-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                isSelected
                                  ? 'bg-gray-900 border-gray-900'
                                  : 'border-gray-300'
                              }`}
                            >
                              {isSelected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                            </span>
                            <span
                              aria-hidden
                              className="h-2.5 w-2.5 rounded-full border flex-shrink-0"
                              style={{
                                backgroundColor: tag.color || '#9ca3af',
                                borderColor: tag.color || '#9ca3af',
                              }}
                            />
                            <span
                              className={`flex-1 text-base ${
                                isSelected ? 'text-gray-900 font-medium' : 'text-gray-700'
                              }`}
                            >
                              {tag.name}
                            </span>
                            {count > 0 && (
                              <span className="text-sm text-gray-400 tabular-nums flex-shrink-0">
                                {count}
                              </span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </div>

            <footer className="flex items-center justify-between px-5 py-4 border-t border-gray-100">
              <button
                type="button"
                onClick={onClearAll}
                disabled={selectedTagIds.size === 0}
                className="text-sm font-semibold text-gray-600 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Clear all
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2 rounded-full bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800"
              >
                Done {selectedTagIds.size > 0 ? `(${selectedTagIds.size})` : ''}
              </button>
            </footer>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
